'use client'

import { motion } from 'framer-motion'
import { ArrowUpRight, Layers } from 'lucide-react'
import Link from 'next/link'
import SmartImage from './ui/SmartImage'
import { siteImages } from '@/data/siteImages'

const cases = [
  { title: 'FinEdge Analytics Platform', category: 'Fintech', img: siteImages.portfolio[0], metric: '+42% engagement', tags: ['Next.js', 'AWS'] },
  { title: 'ShopNova Marketplace', category: 'E-commerce', img: siteImages.portfolio[4], metric: '+38% conversion', tags: ['React', 'Stripe'] },
  { title: 'MediCare+ Patient Portal', category: 'Healthcare', img: siteImages.portfolio[1], metric: 'HIPAA compliant', tags: ['Node.js', 'HL7'] },
  { title: 'LogiFlow Fleet Suite', category: 'Logistics', img: siteImages.portfolio[5], metric: '-27% ops cost', tags: ['IoT', 'Kubernetes'] },
  { title: 'EduSphere LMS', category: 'Education', img: siteImages.portfolio[2], metric: '120k learners', tags: ['React', 'GraphQL'] },
  { title: 'Realty Pulse CRM', category: 'Real Estate', img: siteImages.portfolio[3], metric: '3x lead velocity', tags: ['Next.js', 'Mongo'] },
]

export default function CaseStudies() {
  return (
    <section className="py-20 sm:py-28 bg-sky-50/40 relative overflow-hidden">
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-blue-100/30 rounded-full blur-3xl" />
      <div className="container mx-auto px-4 sm:px-6 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12 sm:mb-16"
        >
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-sky-100 text-sky-600 text-sm font-medium mb-4">
            <Layers className="w-4 h-4" /> Our Work
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-4">
            Featured <span className="gradient-text">Case Studies</span>
          </h2>
          <p className="text-base sm:text-lg text-slate-500 max-w-2xl mx-auto">
            Real products, real outcomes, real growth
          </p>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {cases.map((c, i) => (
            <motion.div
              key={c.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: (i % 3) * 0.1 }}
            >
              <Link
                href="/contact"
                className="group block rounded-2xl overflow-hidden bg-white shadow-sm border border-sky-100/60 hover:shadow-xl hover:-translate-y-1.5 transition-all duration-500 h-full"
              >
                <div className="relative h-48 overflow-hidden">
                  <SmartImage
                    src={c.img}
                    alt={c.title}
                    fill
                    sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
                    className="object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 via-transparent to-transparent" />
                  <span className="absolute top-4 left-4 px-3 py-1 rounded-full bg-white/90 backdrop-blur text-xs font-semibold text-sky-700">
                    {c.category}
                  </span>
                  <span className="absolute bottom-4 left-4 text-white font-semibold text-sm drop-shadow">
                    {c.metric}
                  </span>
                  <div className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/90 backdrop-blur flex items-center justify-center text-sky-600 opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-300">
                    <ArrowUpRight className="w-4 h-4" />
                  </div>
                </div>
                <div className="p-5">
                  <h3 className="font-bold text-slate-900 mb-3 group-hover:text-sky-600 transition-colors">{c.title}</h3>
                  <div className="flex flex-wrap gap-1.5">
                    {c.tags.map((tag) => (
                      <span key={tag} className="px-2 py-0.5 bg-sky-50 text-sky-600 text-xs rounded-md font-medium">{tag}</span>
                    ))}
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  )
}
